import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SERVICE_OPTIONS, CALL_TIMES } from '../data/siteData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = { name: '', company: '', email: '', phone: '', service: '', preferred_time: '', requirement: '' };

export const ContactForm = ({ defaultService = '' }) => {
  const [form, setForm] = useState({ ...initialState, service: defaultService });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data.success) {
        toast.success('Thank you! Our team will reach out within one business day.');
        setForm({ ...initialState, service: defaultService });
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again or email hello@worklynx.co.in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="cf-name" className="text-[#0A192F]">Full Name *</Label>
          <Input id="cf-name" data-testid="contact-input-name" value={form.name} onChange={set('name')} placeholder="Your full name" required className="mt-2" />
        </div>
        <div>
          <Label htmlFor="cf-company" className="text-[#0A192F]">Company Name</Label>
          <Input id="cf-company" data-testid="contact-input-company" value={form.company} onChange={set('company')} placeholder="Your company" className="mt-2" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="cf-email" className="text-[#0A192F]">Email *</Label>
          <Input id="cf-email" data-testid="contact-input-email" type="email" value={form.email} onChange={set('email')} placeholder="you@company.com" required className="mt-2" />
        </div>
        <div>
          <Label htmlFor="cf-phone" className="text-[#0A192F]">Phone Number *</Label>
          <Input id="cf-phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={set('phone')} placeholder="+91 XXXXX XXXXX" required className="mt-2" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-[#0A192F]">Service Required</Label>
          <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
            <SelectTrigger data-testid="contact-select-service" className="mt-2">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#0A192F]">Preferred Call Time</Label>
          <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
            <SelectTrigger data-testid="contact-select-time" className="mt-2">
              <SelectValue placeholder="When should we call?" />
            </SelectTrigger>
            <SelectContent>
              {CALL_TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="cf-req" className="text-[#0A192F]">Tell us about your requirement</Label>
        <Textarea id="cf-req" data-testid="contact-input-requirement" value={form.requirement} onChange={set('requirement')} placeholder="Roles, headcount, cities, timelines…" rows={4} className="mt-2" />
      </div>
      <Button type="submit" data-testid="contact-form-submit" disabled={submitting} size="lg"
        className="w-full bg-[#0A192F] hover:bg-[#0F2A4A] text-white group">
        {submitting ? 'Submitting…' : 'Request a Consultation'}
        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
      </Button>
    </form>
  );
};
